import { apiClient } from './client';

/**
 * 담당자 관리 API — backend `modules/hr/duty.routes.ts` (근태 C1, 2026-08-24).
 *
 * ★★ **부여는 여기 한 곳에서만** 한다. 콘솔(8444)의 「권한 현황」은 보기만 한다 —
 *    *"부여를 두 곳에 두면 어느 쪽이 진짜인지 알 수 없게 된다."*
 * ★ 자동으로 오는 권한(관리자 역할·팀장 지정)은 **여기서 못 바꾼다.** 응답에 읽기 전용으로
 *   함께 와서, 화면이 "이 줄은 자동" 이라고 말할 수 있다.
 * ★ 옛 서버에는 이 라우트가 없다(404) → 부르는 쪽이 사유를 보여 준다.
 */

export const DUTIES = [
  'ATTENDANCE_VIEW', 'ATTENDANCE_APPROVE', 'WORK_SCHEDULE_MANAGE', 'LEAVE_GRANT', 'LABOR_REPORT',
] as const;
export type Duty = (typeof DUTIES)[number];
export type DutyScope = 'ALL' | 'TEAM';

/** 서버 `DUTY_LABEL` 과 같은 말을 쓴다. */
export const DUTY_LABEL: Record<Duty, string> = {
  ATTENDANCE_VIEW: '남의 근태 조회',
  ATTENDANCE_APPROVE: '정정 · 휴가 승인',
  WORK_SCHEDULE_MANAGE: '근무제 · 기준 설정',
  LEAVE_GRANT: '연차 부여',
  LABOR_REPORT: '노무 리포트',
};

export interface DutyGrantDto {
  duty: string;
  scope: string;
}

export interface DutyPersonDto {
  userId: string;
  name: string;
  position: string | null;
  /** 자동으로 오는 권한 — **여기서 못 바꾼다**(사용자 관리에서 역할을 바꿔야 한다). */
  isAdmin: boolean;
  /** 팀장인 팀 이름. 있으면 조회·승인이 **자동으로** 열려 있다. */
  leaderTeamName: string | null;
  duties: DutyGrantDto[];
}

export async function listDuties(): Promise<DutyPersonDto[]> {
  const { data } = await apiClient.get<{ items: DutyPersonDto[] }>('/hr/duties');
  return data.items;
}

/** 한 사람의 업무를 **통째로 맞춘다** — 목록에 없는 것은 회수된다(지우지 않고 끈다). */
export async function setDuties(
  userId: string,
  duties: Array<{ duty: Duty; scope?: DutyScope }>,
): Promise<{ userId: string; duties: DutyGrantDto[] }> {
  const { data } = await apiClient.put<{ userId: string; duties: DutyGrantDto[] }>(
    '/hr/duties', { userId, duties },
  );
  return data;
}

export interface MyDutiesDto {
  grants: DutyGrantDto[];
  isAdmin: boolean;
  isTeamLeader: boolean;
}

/** 내 업무 — 화면이 메뉴를 가르는 데 쓴다(화면 T "직무별 메뉴 분기"). */
export async function getMyDuties(): Promise<MyDutiesDto> {
  const { data } = await apiClient.get<MyDutiesDto>('/hr/duties/me');
  return data;
}
